"use client";

import Link from "next/link";
import { SiGamebanana } from "@/components/icons";
import NewsletterForm from "@/components/NewsletterForm";
import PrivacyChoices from "@/components/PrivacyChoices";
import { TinyshelfBadge } from "@/components/TinyshelfBadge";
import { getAllProducts } from "@/lib/products";
import { getAllTools } from "@/lib/tools";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "@/lib/contact";

export default function Footer() {
  return (
    <footer className="bg-background border-t border-border/50">
      <div className="container mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          {/* Brand & Newsletter Column (Span 5) */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div>
              <Link href="/" className="inline-flex items-center gap-2 mb-4">
                <SiGamebanana className="h-8 w-8 text-primary" aria-hidden="true" />
                <span className="text-xl font-bold tracking-tight">PlugMonkey</span>
              </Link>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-sm">
                We build extensions that give you superpowers on the web. Minimalist, powerful, and
                privacy-focused.
              </p>
            </div>

            {/* Newsletter Subscription */}
            <NewsletterForm source="footer" />

            <div className="flex gap-4">
              <a
                href="https://x.com/plugmonkeyxyz"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-foreground"
                aria-label="Follow PlugMonkey on X, formerly Twitter (opens in new tab)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                </svg>
              </a>
              <a
                href="https://github.com/plugmonkey"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-foreground"
                aria-label="View PlugMonkey on GitHub (opens in new tab)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61583105316936"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-foreground"
                aria-label="Follow PlugMonkey on Facebook (opens in new tab)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/plugmonkeyxyz"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-foreground"
                aria-label="Follow PlugMonkey on Instagram (opens in new tab)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path>
                </svg>
              </a>
              <a
                href="https://www.reddit.com/u/plugmonkeyxyz/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-foreground"
                aria-label="Follow PlugMonkey on Reddit (opens in new tab)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.966-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/plugmonkey/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-foreground"
                aria-label="Follow PlugMonkey on LinkedIn (opens in new tab)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>

            {/* Directory badge — dofollow link back from tinyshelf.co */}
            <TinyshelfBadge />
          </div>

          {/* Links Columns (Span 7) */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8 mt-8 lg:mt-0">
            {/* Products */}
            <div className="flex flex-col gap-4">
              <h3 className="font-bold text-foreground">Pro Extensions</h3>
              <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
                {getAllProducts().map((product) => (
                  <li key={product.id}>
                    <Link
                      href={`/product/${product.slug}/`}
                      className="hover:text-primary transition-colors"
                    >
                      {product.alternateName}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Tools */}
            <div className="flex flex-col gap-4">
              <h3 className="font-bold text-foreground">Free Tools</h3>
              <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
                {getAllTools().map((tool) => (
                  <li key={tool.slug}>
                    <Link
                      href={`/tool/${tool.slug}/`}
                      className="hover:text-primary transition-colors"
                    >
                      {tool.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div className="flex flex-col gap-4">
              <h3 className="font-bold text-foreground">Resources</h3>
              <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
                <li>
                  <Link href="/support/" className="hover:text-primary transition-colors">
                    Support Center
                  </Link>
                </li>
                <li>
                  <a href={SUPPORT_MAILTO} className="hover:text-primary transition-colors">
                    {SUPPORT_EMAIL}
                  </a>
                </li>
                <li>
                  <Link href="/faq/" className="hover:text-primary transition-colors">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link href="/billing/" className="hover:text-primary transition-colors">
                    Manage Subscription
                  </Link>
                </li>
                <li>
                  <Link href="/blog/" className="hover:text-primary transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link href="/alternatives/" className="hover:text-primary transition-colors">
                    Alternatives
                  </Link>
                </li>
                <li>
                  <Link href="/comparisons/" className="hover:text-primary transition-colors">
                    Comparisons
                  </Link>
                </li>
                <li>
                  <Link href="/best/" className="hover:text-primary transition-colors">
                    Roundups
                  </Link>
                </li>
                <li>
                  <Link href="/guides/" className="hover:text-primary transition-colors">
                    Guides
                  </Link>
                </li>
                {/* /for/ + /glossary/ removed from footer 2026-06-29 — both
                    surfaces noindex'd after June 2026 Spam Update (0 + 2 clicks
                    over 3 months respectively). Internal links to noindex pages
                    waste crawl budget and divert PageRank into dead ends. */}
                <li>
                  <Link href="/prompts/" className="hover:text-primary transition-colors">
                    Prompt Library
                  </Link>
                </li>
                {/* /integrations/ removed 2026-10-20 — surface retired (158 impressions,
                    4 clicks across 31 URLs in 16 months, 0 indexed). */}
                <li>
                  <Link href="/downloads/" className="hover:text-primary transition-colors">
                    Download Guides
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div className="flex flex-col gap-4">
              <h3 className="font-bold text-foreground">Company</h3>
              <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
                <li>
                  <Link href="/about/" className="hover:text-primary transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link href="/affiliates/" className="hover:text-primary transition-colors">
                    Affiliate Program
                  </Link>
                </li>
                <li>
                  <Link
                    href="/custom-extension-quote/"
                    className="hover:text-primary transition-colors"
                  >
                    Custom Development
                  </Link>
                </li>

                <li className="pt-4 font-bold text-foreground">Legal</li>
                <li>
                  <Link href="/terms-of-service/" className="hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link href="/privacy-policy/" className="hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <PrivacyChoices />
                </li>
                <li>
                  <Link href="/return-policy/" className="hover:text-primary transition-colors">
                    Return Policy
                  </Link>
                </li>
                <li>
                  <Link href="/accessibility/" className="hover:text-primary transition-colors">
                    Accessibility
                  </Link>
                </li>
                <li>
                  <Link href="/security/" className="hover:text-primary transition-colors">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-border/50 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} PlugMonkey. All rights reserved.</p>
          <div className="flex gap-6">
            <span>Made with 🍌 by monkeys</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
